import { modelOptions, PropType, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { AlertSetting } from "../../../shared.js";
import { WarningType } from "../../../WarningDescription.js";
import {
    AlertSettingSchema,
    getZodSchemaFromClass,
    validate,
    WarningTypeString,
} from "../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type { UserSMSSettingsFieldsWithDefaults, UserSMSSettingsSchemaTypes } from "../../types/userSMSSettings.d.js";
import type { DtoValidatorTypes } from "../../validatableClass.js";

//TODO: Change to UserSmsSettings
const SCHEMA_KEY = "UserSMSSettings" as const;

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<UserSMSSettings>>(...addSchemaKeyHook(SCHEMA_KEY))
export class UserSMSSettings extends SchemaBase<UserSMSSettings, UserSMSSettingsFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;
    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: false })
    public activeStart?: Date;

    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: false })
    public activeEnd?: Date;

    // Nullable to tolerate legacy documents where null entries were stored in the array
    @validate({ zodValidator: z.array(z.string().nullable()).optional() })
    @prop({ type: () => [String], required: true, default: [] }, PropType.ARRAY)
    public phoneNumbers: (string | null)[] = [];

    @validate({ zodValidator: z.partialRecord(WarningTypeString, AlertSettingSchema).optional() })
    @prop({ type: () => Object, required: true, default: { offline: AlertSetting.AddToQueue } })
    public perWarningSettings: Partial<Record<WarningType, AlertSetting>> = {
        offline: AlertSetting.AddToQueue,
    };

    @validate({ zodValidator: z.boolean().optional() })
    @prop({ type: () => Boolean, required: true, default: false })
    public SMSOnFailedVerification: boolean = false;

    static getters = {
        /**
         * Returns whether SMS notifications are configured
         */
        hasSMSConfigured: (self: UserSMSSettings): boolean =>
            Array.isArray(self.phoneNumbers) && self.phoneNumbers.some((n) => n != null && n.length > 0),

        /**
         * Returns whether active hours are configured
         */
        hasActiveHours: (self: UserSMSSettings): boolean => !!(self.activeStart && self.activeEnd),
    } as const satisfies Record<string, (self: UserSMSSettings) => any>;

    static utilities = {
        /**
         * Checks if the current time is within active SMS hours
         */
        isCurrentTimeActive: (self: UserSMSSettings, currentTime: Date): boolean => {
            if (!self.activeStart || !self.activeEnd) {
                return true; // No restrictions means always active
            }

            const sampleTime = new Date(self.activeStart.valueOf());
            sampleTime.setUTCHours(currentTime.getUTCHours());
            sampleTime.setUTCMinutes(currentTime.getUTCMinutes());

            if (
                sampleTime.valueOf() >= self.activeStart.valueOf() &&
                sampleTime.valueOf() <= self.activeEnd.valueOf()
            ) {
                return true;
            }

            const sampleTime2 = new Date(self.activeEnd.valueOf());
            sampleTime2.setUTCHours(currentTime.getUTCHours());
            sampleTime2.setUTCMinutes(currentTime.getUTCMinutes());

            if (
                sampleTime2.valueOf() >= self.activeStart.valueOf() &&
                sampleTime2.valueOf() <= self.activeEnd.valueOf()
            ) {
                return true;
            }

            return false;
        },

        /**
         * Adds a phone number to the SMS settings
         */
        addPhoneNumber: (self: UserSMSSettings, phoneNumber: string): UserSMSSettings => {
            if (!phoneNumber) return self;

            // Don't add duplicates
            if (self.phoneNumbers.some((n) => n === phoneNumber)) {
                return self;
            }

            return {
                ...self,
                phoneNumbers: [...self.phoneNumbers.filter((n) => n != null), phoneNumber],
            };
        },

        /**
         * Updates the alert setting for a specific warning type
         */
        updateAlertSetting: (
            self: UserSMSSettings,
            warningType: WarningType,
            setting: AlertSetting,
        ): UserSMSSettings => ({
            ...self,
            perWarningSettings: {
                ...self.perWarningSettings,
                [warningType]: setting,
            },
        }),
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<UserSMSSettingsSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<UserSMSSettings, UserSMSSettingsFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

UserSMSSettings.dtoValidators = getZodSchemaFromClass(
    UserSMSSettings,
) as unknown as DtoValidatorTypes<UserSMSSettingsSchemaTypes>;
